import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Mail, Lock, Eye, EyeOff, User, Building } from 'lucide-react';
import axios from 'axios';
import AuthLayout from '../components/AuthLayout';

interface LoginData {
  email: string;
  senha: string;
  tipo_usuario: 'cliente' | 'fornecedor';
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tipoUsuario, setTipoUsuario] = useState<'cliente' | 'fornecedor'>('cliente');
  const { register, handleSubmit, formState: { errors } } = useForm<LoginData>();

  const onSubmit = async (data: LoginData) => {
    setIsLoading(true);
    try {
      const endpoint = tipoUsuario === 'cliente' 
        ? '/api/clientes/login'
        : '/api/fornecedores/login';

      const response = await axios.post(endpoint, {
        email: data.email,
        senha: data.senha
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('userType', tipoUsuario);
      
      toast.success('Login realizado com sucesso!');
      
      // Redirecionar baseado no tipo de usuário
      if (tipoUsuario === 'cliente') {
        navigate('/cliente/dashboard');
      } else {
        navigate('/fornecedor/dashboard');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao fazer login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Bem-vindo"
      subtitle="Faça login para acessar sua conta"
    >
      {/* Login Form */}
      <div className="card">
        {/* Tipo de Usuário */}
        <div className="mb-6">
          <label className="form-label">Tipo de Usuário</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setTipoUsuario('cliente')}
              className={`p-4 rounded-lg border-2 transition-all ${
                tipoUsuario === 'cliente'
                  ? 'border-red bg-red/5 text-red'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <User className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-medium">Cliente</p>
                  <p className="text-xs text-gray-500">Buscar peças</p>
                </div>
              </div>
            </button>
            
            <button
              type="button"
              onClick={() => setTipoUsuario('fornecedor')}
              className={`p-4 rounded-lg border-2 transition-all ${
                tipoUsuario === 'fornecedor'
                  ? 'border-red bg-red/5 text-red'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex flex-col items-center space-y-2">
                <Building className="w-6 h-6" />
                <div className="text-center">
                  <p className="font-medium">Fornecedor</p>
                  <p className="text-xs text-gray-500">Vender peças</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="form-label">E-mail</label>
            <div className="flex items-center space-x-2">
              <Mail className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <input
                type="email"
                {...register('email', { required: 'E-mail é obrigatório' })}
                className="form-input flex-1"
                placeholder="seu@email.com"
              />
            </div>
            {errors.email && (
              <p className="text-red text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="form-label">Senha</label>
            <div className="flex items-center space-x-2">
              <Lock className="h-5 w-5 text-gray-400 flex-shrink-0" />
              <input
                type={showPassword ? 'text' : 'password'}
                {...register('senha', { required: 'Senha é obrigatória' })}
                className="form-input flex-1"
                placeholder="Digite sua senha"
              />
            </div>
            {errors.senha && (
              <p className="text-red text-sm mt-1">{errors.senha.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-red focus:ring-red border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 text-sm text-gray-600">
                Lembrar de mim
              </label>
            </div>
            <div className="text-sm">
              <a href="#" className="text-red hover:text-red/80 transition-colors">
                Esqueceu sua senha?
              </a>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn btn-primary w-full"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="spinner mr-3"></div>
                Entrando...
              </div>
            ) : (
              `Entrar como ${tipoUsuario === 'cliente' ? 'Cliente' : 'Fornecedor'}`
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Não tem uma conta?{' '}
            <Link 
              to="/cadastro" 
              className="text-red hover:text-red/80 transition-colors font-medium"
            >
              Cadastre-se
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login; 